import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { LOGO_URL } from "@/lib/constants";
import api, { formatDetail } from "@/lib/api";

export default function ForgotPasswordPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(formatDetail(err.response?.data?.detail) || "Terlalu banyak permintaan. Coba lagi nanti.");
      } else {
        // Untuk error apa pun selain rate limit, tetap tampilkan pesan sukses
        // agar tidak reveal apakah email terdaftar (uniform response).
        setSent(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0B1D3A] text-white relative overflow-hidden">
        <div className="relative z-10">
          <img src={LOGO_URL} alt="PT Eka Nusa Sinergi" className="h-16 bg-white p-3 inline-block" />
        </div>
        <div className="relative z-10 space-y-3">
          <p className="overline text-white/60">Account Recovery</p>
          <h1 className="font-display text-5xl leading-tight tracking-tight">
            Reset Password
            <br /><span className="text-[#FF5A1F]">Anda.</span>
          </h1>
          <p className="text-white/70 max-w-md text-sm leading-relaxed">
            Kami akan mengirim link aman ke email Anda. Link berlaku 30 menit dan hanya dapat dipakai satu kali.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-6 text-xs text-white/50">
          <span>PT. EKA NUSA SINERGI</span>
          <span className="h-1 w-1 bg-white/40" />
          <span>SECURE</span>
        </div>
        <div aria-hidden className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md" data-testid="forgot-password-page">
          <div className="lg:hidden mb-8">
            <img src={LOGO_URL} alt="ENS" className="h-12" />
          </div>
          <p className="overline text-slate-500 mb-3">Lupa Password</p>
          <h2 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Reset Akun Anda</h2>
          <p className="text-sm text-slate-500 mt-2">
            Masukkan email perusahaan Anda. Jika terdaftar, kami akan mengirim link reset.
          </p>

          {sent ? (
            <div data-testid="forgot-password-success" className="mt-8 space-y-4">
              <div className="border-l-4 border-[#FF5A1F] bg-orange-50 p-4">
                <p className="text-sm font-semibold text-[#0B1D3A]">Link telah dikirim (jika email terdaftar).</p>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                  Silakan cek inbox Anda dalam beberapa menit. Link berlaku <strong>30 menit</strong> dan hanya dapat dipakai satu kali. Cek folder Spam jika tidak terlihat.
                </p>
              </div>
              <Link to="/login" data-testid="forgot-password-back-to-login" className="inline-block text-xs font-semibold uppercase tracking-wider text-[#FF5A1F] hover:text-[#e04a13]">
                &larr; Kembali ke Login
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="fp-email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
                  <Input id="fp-email" data-testid="forgot-password-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@ensinergi.com" className="rounded-none h-11 border-slate-300" />
                </div>
                <Button type="submit" data-testid="forgot-password-submit" disabled={busy} className="w-full rounded-none h-11 bg-[#FF5A1F] hover:bg-[#e04a13] text-white font-semibold uppercase tracking-wider text-sm">
                  {busy ? "Mengirim…" : "Kirim Link Reset"}
                </Button>
              </form>

              <div className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500">
                <Link to="/login" data-testid="forgot-password-back-link" className="font-semibold text-[#FF5A1F] hover:text-[#e04a13]">
                  &larr; Kembali ke Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
